import {signal} from '@angular/core';
import type {ChildrenOutletContexts} from '@angular/router';
import {DebugCard, DebugNode} from '../model/debug-card';

interface NgGlobalDebug {
  getComponent?: (el: Element) => unknown;
}

const MAX_TREE_DEPTH = 50;

function ngDebug(): NgGlobalDebug | undefined {
  const w = typeof window !== 'undefined' ? (window as unknown as {ng?: NgGlobalDebug}) : undefined;
  const ng = w?.ng;
  return ng && typeof ng.getComponent === 'function' ? ng : undefined;
}

function routedHostElement(doc: Document): Element | null {
  let found: Element | null = null;
  for (const outlet of Array.from(doc.querySelectorAll('router-outlet'))) {
    if (outlet.nextElementSibling) {
      found = outlet.nextElementSibling;
    }
  }
  return found;
}

function leafActivatedInstance(contexts: ChildrenOutletContexts | null | undefined): unknown {
  if (!contexts) return null;
  const ctx = contexts.getContext('primary');
  if (!ctx?.outlet?.isActivated) return null;
  const here = (ctx.outlet as {component?: unknown}).component ?? null;
  const deeper = leafActivatedInstance(ctx.children);
  return deeper ?? here;
}

/**
 * Returns true for UI-library components (PrimeNG, CDK, etc.) that inject
 * el/cd/renderer as own fields. These should never win over app components.
 */
function isLibraryComponent(instance: unknown): boolean {
  if (!instance || typeof instance !== 'object') return false;
  const keys = new Set(Object.keys(instance as object));
  return keys.has('el') && keys.has('cd') && keys.has('renderer');
}

/**
 * Score an instance by how many own non-service, non-function properties it has.
 * Higher score = more interesting data for the user to inspect.
 */
function dataScore(instance: unknown): number {
  if (isLibraryComponent(instance)) return 0;
  if (!instance || typeof instance !== 'object') return 0;
  let score = 0;
  try {
    for (const key of Object.keys(instance as object)) {
      let v: unknown;
      try {
        v = (instance as Record<string, unknown>)[key];
      } catch {
        continue;
      }
      // unwrap signals (functions with SIGNAL symbol)
      if (typeof v === 'function') {
        try {
          const isSig = Object.getOwnPropertySymbols(v).some((s) => s.description === 'SIGNAL');
          if (isSig) v = (v as () => unknown)();
          else continue;
        } catch {
          continue;
        }
      }
      if (v === null || v === undefined) continue;
      // skip angular services / observables
      const ctor = (v as {constructor?: {ɵprov?: unknown; ɵfac?: unknown; name?: string}})
        .constructor as {ɵprov?: unknown; ɵfac?: unknown; name?: string} | undefined;
      if (ctor && (ctor.ɵprov || ctor.ɵfac)) continue;
      if (ctor?.name && /Service$/.test(ctor.name)) continue;
      // reward arrays with items and plain objects
      if (Array.isArray(v)) score += (v as unknown[]).length > 0 ? 5 : 1;
      else if (typeof v === 'object') score += 3;
      else score += 1;
    }
  } catch {
    // ignore
  }
  return score;
}

/**
 * Walk the full DOM subtree under `root`, call ng.getComponent on every element,
 * and return the component instance with the highest data score.
 * This finds the real "page" component even when it's not directly rendered by a router outlet
 * (e.g. dynamically loaded step components inside a sequence wrapper).
 */
function richestComponentInSubtree(root: Element, ng: NgGlobalDebug): unknown {
  let best: unknown = null;
  let bestScore = -1;

  // depth penalty: sub-components deeper in the tree score lower so the
  // actual page/step component (shallow) wins over its own child components
  function walk(el: Element, depth: number): void {
    const instance = safeGetComponent(ng, el);
    if (instance) {
      const score = dataScore(instance) - depth * 2;
      if (score > bestScore) {
        bestScore = score;
        best = instance;
      }
    }
    for (const child of Array.from(el.children)) {
      walk(child, depth + (instance ? 1 : 0));
    }
  }

  walk(root, 0);
  return best;
}

export function discoverRoutedComponent(
  doc: Document,
  contexts: ChildrenOutletContexts | null,
): unknown {
  const ng = ngDebug();
  const host = routedHostElement(doc);

  // Dev mode: walk the full component subtree and find the richest data component.
  // This handles apps where steps/pages are rendered dynamically inside a wrapper
  // rather than through nested router outlets.
  if (ng && host) {
    const richest = richestComponentInSubtree(host, ng);
    if (richest) return richest;
  }

  // Fallback: router context chain (works in prod, or when ng is unavailable)
  return leafActivatedInstance(contexts) ?? (host ? safeGetComponent(ng!, host) : null);
}

export function discoverComponentTree(
  doc: Document,
  contexts: ChildrenOutletContexts | null,
): DebugCard | null {
  const ng = ngDebug();
  const host = routedHostElement(doc);
  const rootInstance =
    (ng && host ? safeGetComponent(ng, host) : null) ?? leafActivatedInstance(contexts);

  if (!rootInstance && !host) return null;

  const rootName = componentName(rootInstance) ?? host?.tagName.toLowerCase() ?? 'Page';
  const rootTag = host?.tagName.toLowerCase() ?? '';

  const childRows: DebugNode[] =
    ng && host
      ? childComponentNodes(host, ng, 2, 0)
      : [{field: '(child components)', depth: 2, value: 'discovery available in dev mode only'}];

  // Root component as first row — clickable to inspect it
  const rootRow: DebugNode = {
    field: rootName,
    depth: 1,
    className: rootTag,
    children: childRows,
    summary: `${childRows.length} child component${childRows.length === 1 ? '' : 's'}`,
    expanded: signal(true),
    instance: rootInstance ?? undefined,
  };

  return {
    title: 'Component Tree',
    className: '',
    rows: [rootRow],
    expanded: signal(true),
  };
}

function childComponentNodes(
  el: Element,
  ng: NgGlobalDebug,
  depth: number,
  treeDepth: number,
): DebugNode[] {
  if (treeDepth >= MAX_TREE_DEPTH) return [];
  return directChildHosts(el, ng).map((child) => componentNode(child, ng, depth, treeDepth));
}

function componentNode(
  el: Element,
  ng: NgGlobalDebug,
  depth: number,
  treeDepth: number,
): DebugNode {
  const tag = el.tagName.toLowerCase();
  const instance = safeGetComponent(ng, el);
  const name = componentName(instance) ?? tag;
  const kids = childComponentNodes(el, ng, depth + 1, treeDepth + 1);
  if (kids.length === 0) return {field: name, depth, value: tag, instance: instance ?? undefined};
  return {
    field: name,
    depth,
    className: tag,
    summary: `${kids.length} child component${kids.length === 1 ? '' : 's'}`,
    children: kids,
    expanded: signal(true),
    instance: instance ?? undefined,
  };
}

function directChildHosts(el: Element, ng: NgGlobalDebug): Element[] {
  const out: Element[] = [];
  for (const child of Array.from(el.children)) {
    if (safeGetComponent(ng, child)) {
      out.push(child);
    } else {
      out.push(...directChildHosts(child, ng));
    }
  }
  return out;
}

function componentName(cmp: unknown): string | null {
  const ctor = (cmp as {constructor?: {name?: string}} | null)?.constructor;
  return ctor?.name || null;
}

function safeGetComponent(ng: NgGlobalDebug, el: Element): unknown {
  try {
    return ng.getComponent!(el) ?? null;
  } catch {
    return null;
  }
}
